import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';
import { getTodayDateInputValue } from '../utils/date';

function PlanillaCobranzaZona() {
  const { API_URL } = useAuth();
  const fechaActual = new Date();
  const anioActual = fechaActual.getFullYear();

  const [mes, setMes] = useState(fechaActual.getMonth() + 1);
  const [anio, setAnio] = useState(anioActual);
  const [generando, setGenerando] = useState(false);

  const hoy = getTodayDateInputValue();
  const [cobrosDesde, setCobrosDesde] = useState(hoy);
  const [cobrosHasta, setCobrosHasta] = useState(hoy);
  const [generandoCobrosDiarios, setGenerandoCobrosDiarios] = useState(false);

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
  });

  const meses = useMemo(
    () => [
      { value: 1, label: 'Enero' },
      { value: 2, label: 'Febrero' },
      { value: 3, label: 'Marzo' },
      { value: 4, label: 'Abril' },
      { value: 5, label: 'Mayo' },
      { value: 6, label: 'Junio' },
      { value: 7, label: 'Julio' },
      { value: 8, label: 'Agosto' },
      { value: 9, label: 'Septiembre' },
      { value: 10, label: 'Octubre' },
      { value: 11, label: 'Noviembre' },
      { value: 12, label: 'Diciembre' },
    ],
    [],
  );

  const anios = useMemo(
    () => [anioActual - 2, anioActual - 1, anioActual, anioActual + 1],
    [anioActual],
  );

  const cerrarModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const descargarCobrosDiarios = async () => {
    if (!cobrosDesde || !cobrosHasta) {
      setModalConfig({
        isOpen: true,
        type: 'warning',
        title: 'Fechas requeridas',
        message: 'Seleccione un rango de fechas para generar la planilla.',
        onConfirm: cerrarModal,
      });
      return;
    }
    setGenerandoCobrosDiarios(true);
    try {
      const response = await axios.get(`${API_URL}/estadisticas/cobros-diarios.pdf`, {
        params: { fechaDesde: cobrosDesde, fechaHasta: cobrosHasta },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cobros_diarios_${cobrosDesde}_${cobrosHasta}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setModalConfig({
        isOpen: true,
        type: 'success',
        title: 'Planilla generada',
        message: `Se descargó la planilla de cobros diarios del ${cobrosDesde} al ${cobrosHasta}.`,
        onConfirm: cerrarModal,
      });
    } catch (error) {
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Error al generar PDF',
        message: error.response?.data?.message || 'No se pudo generar la planilla. Intente nuevamente.',
        onConfirm: cerrarModal,
      });
    } finally {
      setGenerandoCobrosDiarios(false);
    }
  };

  const descargarPlanilla = async () => {
    if (!mes || !anio) {
      setModalConfig({
        isOpen: true,
        type: 'warning',
        title: 'Periodo requerido',
        message: 'Seleccione mes y año para generar la planilla.',
        onConfirm: cerrarModal,
      });
      return;
    }

    setGenerando(true);
    try {
      const response = await axios.get(`${API_URL}/estadisticas/planilla-cobranza-zona.pdf`, {
        params: { mes, anio },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `planilla_cobranza_zona_${anio}_${String(mes).padStart(2, '0')}.pdf`,
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const mesLabel = meses.find((item) => item.value === Number(mes))?.label;
      setModalConfig({
        isOpen: true,
        type: 'success',
        title: 'Planilla generada',
        message: `Se descargó la planilla de cobranza para ${mesLabel} ${anio}.`,
        onConfirm: cerrarModal,
      });
    } catch (error) {
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Error al generar PDF',
        message:
          error.response?.data?.message ||
          'No se pudo generar la planilla de cobranza por zona. Intente nuevamente.',
        onConfirm: cerrarModal,
      });
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="flex h-screen bg-background-light dark:bg-background-dark">
      <Sidebar />

      <main className="flex-1 overflow-y-auto p-8">
        <div className="max-w-4xl">
          <div className="mb-7">
            <h1 className="text-3xl font-bold text-[#111618] dark:text-white">
              Planilla de Cobranza por Zona
            </h1>
            <p className="text-[#617c89] dark:text-gray-400 mt-2">
              Exporta un PDF con resumen por zona y detalle de usuarios que pagaron o faltan pagar.
            </p>
          </div>

          <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-[#111618] dark:text-white mb-2">
                  Mes
                </label>
                <select
                  value={mes}
                  onChange={(e) => setMes(Number(e.target.value))}
                  className="w-full h-11 px-4 rounded-lg bg-[#f0f3f4] dark:bg-gray-700 border-none text-[#111618] dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {meses.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111618] dark:text-white mb-2">
                  Año
                </label>
                <select
                  value={anio}
                  onChange={(e) => setAnio(Number(e.target.value))}
                  className="w-full h-11 px-4 rounded-lg bg-[#f0f3f4] dark:bg-gray-700 border-none text-[#111618] dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {anios.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={descargarPlanilla}
                  disabled={generando}
                  className="w-full h-11 flex items-center justify-center gap-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generando ? (
                    <>
                      <span className="material-symbols-outlined animate-spin">progress_activity</span>
                      Generando...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">download</span>
                      Generar PDF
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                El PDF incluye:
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                <li>Resumen por zona: pagaron, faltan pagar y total de usuarios.</li>
                <li>Dos hojas de detalle: una de usuarios que pagaron y otra de usuarios pendientes.</li>
                <li>Estado considerado como pagado solo cuando la boleta está en estado PAGADA.</li>
              </ul>
            </div>
          </div>

          {/* Cobros diarios */}
          <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6 mt-6">
            <div className="mb-5">
              <h2 className="text-xl font-bold text-[#111618] dark:text-white">
                Cobros Diarios por fecha
              </h2>
              <p className="text-[#617c89] dark:text-gray-400 mt-1">
                Exporta un PDF con los cobros presenciales y los comprobantes aprobados desde administración en el período seleccionado, organizado por zona.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-[#111618] dark:text-white mb-2">
                  Desde
                </label>
                <input
                  type="date"
                  value={cobrosDesde}
                  onChange={(e) => setCobrosDesde(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg bg-[#f0f3f4] dark:bg-gray-700 border-none text-[#111618] dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111618] dark:text-white mb-2">
                  Hasta
                </label>
                <input
                  type="date"
                  value={cobrosHasta}
                  onChange={(e) => setCobrosHasta(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg bg-[#f0f3f4] dark:bg-gray-700 border-none text-[#111618] dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={descargarCobrosDiarios}
                  disabled={generandoCobrosDiarios}
                  className="w-full h-11 flex items-center justify-center gap-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generandoCobrosDiarios ? (
                    <>
                      <span className="material-symbols-outlined animate-spin">progress_activity</span>
                      Generando...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">download</span>
                      Generar PDF
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-4">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
                El PDF incluye:
              </p>
              <ul className="text-sm text-emerald-700 dark:text-emerald-300 list-disc list-inside space-y-1">
                <li>Resumen por zona: cantidad de cobros y total recaudado.</li>
                <li>Una hoja por zona con detalle de cada cobro: padrón, cliente, dirección, boleta, fecha y monto.</li>
                <li>Incluye cobros presenciales y comprobantes web aprobados en el rango de fechas.</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={cerrarModal}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
        onConfirm={modalConfig.onConfirm}
      />
    </div>
  );
}

export default PlanillaCobranzaZona;
